import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import DataTable from "../../components/DataTable";
import { api } from "../../axios/api";
import { Calendar, Users, ArrowUpRight, ShieldCheck, RefreshCw, DollarSign, FileText, ClipboardList, Printer, X } from "lucide-react";

const RelatorioUsuario = () => {
    const user = useSelector((state) => state.user.user);
    const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");
    const empresaId = user?.empresa_id;

    const baseURL = api.defaults.baseURL || "http://localhost:3000";

    const hojeStr = new Date().toISOString().split('T')[0];
    const primeiroDiaMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];

    const [dataInicio, setDataInicio] = useState(primeiroDiaMes);
    const [dataFim, setDataFim] = useState(hojeStr);
    const [usuarios, setUsuarios] = useState([]);
    const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
    
    const [dadosOperadores, setDadosOperadores] = useState([]);
    const [totaisCards, setTotaisCards] = useState({
        documentos: 0,
        faturado_bruto: 0,
        iva: 0,
        retencao: 0,
        caixa_liquido: 0
    });
    const [loading, setLoading] = useState(false);

    const [modalAberto, setModalAberto] = useState(false);
    const [impressoras, setImpressoras] = useState([]);
    const [loadingImpressoras, setLoadingImpressoras] = useState(false);
    const [loadingImpressao, setLoadingImpressao] = useState(false);

    const texts = {
        "pt-AO": {
            title: "Produtividade por Utilizador",
            subtitle: "Acompanhe o volume faturado, quantidade de emissões, impostos e movimentação de caixa por operador",
            lblStart: "Data Inicial",
            lblEnd: "Data Final",
            lblUser: "Filtrar por Utilizador",
            lblTodos: "Todos os utilizadores (Ranking)...",
            btnFilter: "Filtrar",
            btnPdf: "Imprimir Relatório",
            btnTicket: "Imprimir Ticket",
            cardDocs: "Total Emissões",
            cardFaturado: "Volume Bruto Faturado",
            cardIva: "Total IVA Arrecadado",
            cardCaixa: "Fluxo Líquido de Caixa",
            colUser: "UTILIZADOR / OPERADOR",
            colQtd: "QTD DOCS",
            colIva: "IVA",
            colRetencao: "RETENÇÃO",
            colBruto: "VOL. BRUTO",
            colCaixa: "CAIXA REAL",
            modalTitle: "Selecionar Impressora",
            modalSubtitle: "Escolha uma impressora térmica ativa para imprimir o ticket de produtividade",
            btnEnviarImpressao: "Imprimir Agora",
            lblNenhumaImpressora: "Nenhuma impressora encontrada para esta empresa."
        }
    };

    const t = texts[language] || texts["pt-AO"];

    useEffect(() => {
        if (empresaId) {
            api.get(`/auth/users/empresa/${empresaId}`)
                .then(res => {
                    const dados = res.data || [];
                    const options = dados.map(u => ({
                        value: u.id,
                        label: u.nome ? u.nome.toUpperCase() : "OPERADOR SEM NOME"
                    }));
                    setUsuarios(options);
                })
                .catch(err => console.error("Erro ao buscar utilizadores via auth:", err));
        }
    }, [empresaId]);

    const fetchProdutividade = async () => {
        setLoading(true);
        try {
            const params = { data_inicio: dataInicio, data_fim: dataFim };
            if (usuarioSelecionado) params.user_id = usuarioSelecionado.value;

            const response = await api.get(`/app/relatorios/utilizador/${empresaId}`, { params });
            
            if (response.data) {
                setDadosOperadores(response.data.usuarios || []);
                setTotaisCards(response.data.totais_gerais || { documentos: 0, faturado_bruto: 0, iva: 0, retencao: 0, caixa_liquido: 0 });
            }
        } catch (error) {
            console.error("Erro ao processar relatório de produtividade:", error);
        } finally {
            setLoading(false);
        }
    };

    const abrirModalImpressora = async () => {
        if (dadosOperadores.length === 0) return;
        setModalAberto(true);
        setLoadingImpressoras(true);
        try {
            const res = await api.get(`/app/impressoras/empresa/${empresaId}`);
            setImpressoras(res.data || []);
        } catch (error) {
            console.error("Erro ao carregar mapeamento de impressoras:", error);
        } finally {
            setLoadingImpressoras(false);
        }
    };

    const dispararImpressaoTicket = async (impressoraId) => {
        setLoadingImpressao(true);
        try {
            await api.post(`/app/prints/ticket-produtividade-usuario`, {
                empresa_id: empresaId,
                user_id: usuarioSelecionado ? usuarioSelecionado.value : null,
                data_inicio: dataInicio,
                data_fim: dataFim,
                impressora_id: impressoraId
            });
            setModalAberto(false);
        } catch (error) {
            console.error("Erro ao emitir ticket na impressora térmica:", error);
            alert("Não foi possível estabelecer comunicação com a impressora térmica selecionada.");
        } finally {
            setLoadingImpressao(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat("pt-AO", { style: "currency", currency: "AOA" }).format(val || 0);
    };

    const printUrl = `${baseURL}/prints/relatorio-vendas-user?empresa_id=${empresaId}${usuarioSelecionado ? `&user_id=${usuarioSelecionado.value}` : ''}&data_inicio=${dataInicio}&data_fim=${dataFim}`;

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderRadius: "0px",
            border: state.isFocused ? "1px solid #0078d4" : "1px solid #8a8886",
            boxShadow: "none",
            minHeight: "34px",
            height: "34px",
            fontSize: "13px",
            background: "#fff",
            '&:hover': { borderColor: state.isFocused ? "#0078d4" : "#323130" }
        }),
        valueContainer: (provided) => ({ ...provided, padding: "0px 8px", height: "34px" }),
        indicatorsContainer: (provided) => ({ ...provided, height: "34px" }),
        menu: (provided) => ({ ...provided, borderRadius: "0px", marginTop: "1px", zIndex: 9999 }),
        option: (provided, state) => ({
            ...provided,
            fontSize: "13px",
            backgroundColor: state.isSelected ? "#0078d4" : state.isFocused ? "#f3f2f1" : "#fff",
            color: state.isSelected ? "#fff" : "#323130",
            cursor: "pointer",
            '&:active': { backgroundColor: "#0078d4", color: "#fff" }
        })
    };

    return (
        <div id="layout-wrapper">
            <Navbar />
            <Sidebar />

            <style>{`
                .win10-layout { min-height: 90vh; padding: 0px 0px; }
                .win10-panel-title { font-size: 18px; color: #323130; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
                .win10-card-main { background: #fff; border: 1px solid #e1e1e1; border-radius: 0px; padding: 20px; margin-bottom: 25px; }
                .kpi-container { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
                .kpi-box { background: #faf9f8; border: 1px solid #d2d0ce; padding: 15px 18px; flex: 1; min-width: 200px; border-left: 4px solid #605e5c; }
                .kpi-box.kpi-faturado { border-left-color: #0078d4; }
                .kpi-box.kpi-iva { border-left-color: #b4009e; }
                .kpi-box.kpi-caixa { border-left-color: #107c41; }
                .kpi-title { font-size: 11px; color: #605e5c; text-transform: uppercase; font-weight: bold; }
                .kpi-value { font-size: 19px; color: #323130; font-weight: 700; font-family: monospace; margin-top: 4px; }
                .win10-input { border-radius: 0px; border: 1px solid #8a8886; font-size: 13px; padding: 5px 10px; height: 34px; background: #fff; }
                .win10-btn-filter { background: #323130; color: #fff; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; border: none; text-transform: uppercase; transition: 0.1s; display: inline-flex; align-items: center; gap: 6px; }
                .win10-btn-filter:hover { background: #201f1e; }
                .win10-link-pdf { background: #a80000; color: #fff !important; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; display: inline-flex; align-items: center; gap: 8px; text-transform: uppercase; text-decoration: none; transition: 0.1s; border: none; }
                .win10-link-pdf:hover { background: #800000; text-decoration: none; }
                .win10-link-pdf.disabled { background: #f3f2f1; color: #a19f9d !important; cursor: not-allowed; pointer-events: none; }
                .win10-btn-ticket { background: #107c41; color: #fff; border-radius: 0px; font-size: 12px; font-weight: 600; height: 34px; padding: 0 20px; display: inline-flex; align-items: center; gap: 8px; text-transform: uppercase; transition: 0.1s; border: none; cursor: pointer; }
                .win10-btn-ticket:hover { background: #0b592e; }
                .win10-btn-ticket:disabled { background: #f3f2f1; color: #a19f9d; cursor: not-allowed; }
                .win10-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 10050; }
                .win10-modal-box { background: #fff; border: 1px solid #0078d4; min-width: 450px; box-shadow: 0 4px 15px rgba(0,0,0,0.2); border-radius: 0px; padding: 0; }
                .win10-modal-header { background: #f3f2f1; padding: 10px 15px; border-bottom: 1px solid #e1e1e1; display: flex; justify-content: space-between; align-items: center; }
                .win10-modal-body { padding: 20px; }
                .printer-row { padding: 10px; border: 1px solid #d2d0ce; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; transition: 0.1s; }
                .printer-row:hover { border-color: #0078d4; background: #f3f2f1; }
            `}</style>

            <main className="app-wrapper animate__animated animate__fadeIn">
                <div className="win10-layout">
                    
                    <div className="win10-card-main shadow-sm">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 border-bottom pb-3">
                            <div>
                                <h2 className="win10-panel-title m-0 d-flex align-items-center gap-2">
                                    <Users size={20} className="text-dark"/> {t.title}
                                </h2>
                                <p className="text-muted small m-0 mt-1">{t.subtitle}</p>
                            </div>
                            
                            <div className="d-flex align-items-end gap-2 flex-wrap">
                                <div className="d-flex flex-column" style={{ minWidth: "250px" }}>
                                    <label className="text-muted small fw-bold mb-1">{t.lblUser}</label>
                                    <Select 
                                        options={usuarios}
                                        value={usuarioSelecionado}
                                        onChange={(option) => setUsuarioSelecionado(option)}
                                        placeholder={t.lblTodos}
                                        isClearable={true}
                                        styles={customSelectStyles}
                                    />
                                </div>
                                <div className="d-flex flex-column">
                                    <label className="text-muted small fw-bold mb-1">{t.lblStart}</label>
                                    <input type="date" className="form-control win10-input" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
                                </div>
                                <div className="d-flex flex-column">
                                    <label className="text-muted small fw-bold mb-1">{t.lblEnd}</label>
                                    <input type="date" className="form-control win10-input" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
                                </div>
                                
                                <button className="win10-btn-filter" onClick={fetchProdutividade} disabled={loading}>
                                    {loading ? <RefreshCw size={14} className="animate-spin" /> : <Calendar size={14}/>} 
                                    {t.btnFilter}
                                </button>

                                <a 
                                    href={printUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className={`win10-link-pdf ${dadosOperadores.length === 0 ? "disabled" : ""}`}
                                >
                                    <FileText size={14}/>
                                    {t.btnPdf}
                                </a>

                            </div>
                        </div>

                        <div className="kpi-container">
                            <div className="kpi-box">
                                <div className="kpi-title d-flex align-items-center gap-1"><ClipboardList size={13}/> {t.cardDocs}</div>
                                <div className="kpi-value">{totaisCards.documentos}</div>
                            </div>
                            <div className="kpi-box kpi-faturado">
                                <div className="kpi-title d-flex align-items-center gap-1"><ArrowUpRight size={13}/> {t.cardFaturado}</div>
                                <div className="kpi-value">{formatCurrency(totaisCards.faturado_bruto)}</div>
                            </div>
                            <div className="kpi-box kpi-iva">
                                <div className="kpi-title d-flex align-items-center gap-1"><ShieldCheck size={13}/> {t.cardIva}</div>
                                <div className="kpi-value">{formatCurrency(totaisCards.iva)}</div>
                            </div>
                            <div className="kpi-box kpi-caixa">
                                <div className="kpi-title d-flex align-items-center gap-1"><DollarSign size={13}/> {t.cardCaixa}</div>
                                <div className="kpi-value">{formatCurrency(totaisCards.caixa_liquido)}</div>
                            </div>
                        </div>

                        <div className="mt-3">
                            <DataTable 
                                columns={[
                                    { 
                                        title: t.colUser, 
                                        data: "usuario",
                                        render: (usuario) => `<span class="fw-bold text-dark">${usuario.nome}</span><br/><small class="text-muted">${usuario.email}</small>`
                                    },
                                    { 
                                        title: t.colQtd, 
                                        data: "quantidade_documentos",
                                        render: (qtd) => `<span class="font-mono fw-bold">${qtd} docs</span>`
                                    },
                                    { 
                                        title: t.colIva, 
                                        data: "total_iva",
                                        render: (iva) => `<span class="font-mono text-muted">${formatCurrency(iva)}</span>`
                                    },
                                    { 
                                        title: t.colRetencao, 
                                        data: "total_retencao",
                                        render: (ret) => `<span class="font-mono text-primary">${formatCurrency(ret)}</span>`
                                    },
                                    { 
                                        title: t.colBruto, 
                                        data: "volume_bruto",
                                        render: (bruto) => `<span class="font-mono text-dark fw-bold">${formatCurrency(bruto)}</span>`
                                    },
                                    { 
                                        title: t.colCaixa, 
                                        data: "caixa_dinheiro_puro",
                                        render: (caixa) => `<span class="font-mono text-success fw-bold">${formatCurrency(caixa)}</span>`
                                    }
                                ]}
                                data={dadosOperadores}
                            />
                        </div>
                    </div>
                </div>
            </main>

            {modalAberto && (
                <div className="win10-modal-overlay">
                    <div className="win10-modal-box">
                        <div className="win10-modal-header">
                            <div>
                                <h5 className="m-0 text-dark fw-bold" style={{fontSize: "14px"}}>{t.modalTitle}</h5>
                            </div>
                            <button className="btn p-0 border-0" onClick={() => setModalAberto(false)} disabled={loadingImpressao}>
                                <X size={18} className="text-muted"/>
                            </button>
                        </div>
                        <div className="win10-modal-body">
                            <p className="text-muted small mb-3">{t.modalSubtitle}</p>
                            
                            {loadingImpressoras ? (
                                <div className="text-center py-4">
                                    <RefreshCw size={24} className="animate-spin text-primary mx-auto" />
                                    <span className="text-muted small mt-2 d-block">Mapeando impressoras ativas...</span>
                                </div>
                            ) : impressoras.length === 0 ? (
                                <div className="alert alert-warning border-0 small rounded-0">{t.lblNenhumaImpressora}</div>
                            ) : (
                                <div style={{maxHeight: "250px", overflowY: "auto"}}>
                                    {impressoras.map((printer) => (
                                        <div key={printer.id} className="printer-row">
                                            <div>
                                                <div className="fw-bold text-dark small d-flex align-items-center gap-1">
                                                    <Printer size={13}/> {printer.nome.toUpperCase()}
                                                </div>
                                                <small className="text-muted" style={{fontSize: "11px"}}>Caminho / IP: {printer.porta || "Local USB"}</small>
                                            </div>
                                            <button 
                                                className="win10-btn-filter" 
                                                style={{height: "28px", fontSize: "11px", padding: "0 12px"}}
                                                onClick={() => dispararImpressaoTicket(printer.id)}
                                                disabled={loadingImpressao}
                                            >
                                                {loadingImpressao ? "Enviando..." : t.btnEnviarImpressao}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default RelatorioUsuario;